import { useEffect, useMemo, useRef } from 'react';

import { isCommunityCCRC } from 'components/Community/utils';

import { COMMUNITY_CONTRACT_SELECTION_TYPE } from '../constants';
import { CommunityContract } from '../types/contracts';

type CommunityLike = {
  id: number;
  rental_types?: number[];
  contracts?: CommunityContract[];
};

type CompetitorLike = {
  contracts?: CommunityContract[];
};

type UseOverviewContractDefaultsParams = {
  community?: CommunityLike;
  competitors?: CompetitorLike[];
  viewFilters?: any;
  setViewFilters: (updater: (prev: any) => any) => void;
};

const REFUND_NOT_AVAILABLE_KEY = '__NO_REFUND__';

const getCommunityContractsByType = (contracts: CommunityContract[]) => {
  return contracts.reduce<Record<number, CommunityContract[]>>((acc, contract) => {
    const items = acc[contract.contract_id] || [];
    items.push(contract);
    acc[contract.contract_id] = items;
    return acc;
  }, {});
};

const pickMostCommonContractType = (
  communityContractsByType: Record<number, CommunityContract[]>,
  competitors: CompetitorLike[] = []
) => {
  const competitorTypeCounts = competitors.reduce<Record<number, number>>((acc, competitor) => {
    (competitor.contracts || []).forEach((contract) => {
      if (communityContractsByType[contract.contract_id]) {
        acc[contract.contract_id] = (acc[contract.contract_id] || 0) + 1;
      }
    });
    return acc;
  }, {});

  const typeEntries = Object.entries(competitorTypeCounts).sort((a, b) => Number(b[1]) - Number(a[1]));
  if (typeEntries.length > 0) {
    return Number(typeEntries[0][0]);
  }

  const fallbackType = Object.keys(communityContractsByType)[0];
  return fallbackType ? Number(fallbackType) : undefined;
};

const pickMostCommonRefund = (selectedType: number, competitors: CompetitorLike[] = []) => {
  const refundCounts = competitors.reduce<Record<string, number>>((acc, competitor) => {
    (competitor.contracts || [])
      .filter((contract) => contract.contract_id === selectedType)
      .forEach((contract) => {
        const refundKey =
          contract.refund_percentage === null || contract.refund_percentage === undefined
            ? REFUND_NOT_AVAILABLE_KEY
            : contract.refund_percentage.toString();
        acc[refundKey] = (acc[refundKey] || 0) + 1;
      });
    return acc;
  }, {});

  const refundEntries = Object.entries(refundCounts).sort((a, b) => Number(b[1]) - Number(a[1]));
  if (refundEntries.length > 0) {
    return refundEntries[0][0];
  }
  return undefined;
};

const resolveRefundKeyToValue = (refundKey?: string) => {
  if (!refundKey) return undefined;
  if (refundKey === REFUND_NOT_AVAILABLE_KEY) {
    return null;
  }
  return Number(refundKey);
};

const findMatchingCommunityContract = (
  contracts: CommunityContract[],
  refundValue: number | null | undefined
): CommunityContract | undefined => {
  if (refundValue === undefined) {
    return undefined;
  }

  if (refundValue === null) {
    return contracts.find(
      (contract) => contract.refund_percentage === null || contract.refund_percentage === undefined
    );
  }

  return contracts.find((contract) => contract.refund_percentage === refundValue);
};

const getDefaultContract = (
  community?: CommunityLike,
  competitors?: CompetitorLike[]
): CommunityContract | undefined => {
  const communityContracts = community?.contracts || [];
  if (!communityContracts.length) {
    return undefined;
  }

  const contractsByType = getCommunityContractsByType(communityContracts);
  const selectedType = pickMostCommonContractType(contractsByType, competitors || []);
  if (selectedType === undefined) {
    return communityContracts[0];
  }

  const contractsForType = contractsByType[selectedType] || [];
  if (!contractsForType.length) {
    return communityContracts[0];
  }

  const refundKey = pickMostCommonRefund(selectedType, competitors || []);
  const refundValue = resolveRefundKeyToValue(refundKey);

  const matchedContract = findMatchingCommunityContract(contractsForType, refundValue);
  if (matchedContract) {
    return matchedContract;
  }

  return contractsForType[0] || communityContracts[0];
};

export const useOverviewContractDefaults = ({
  community,
  competitors,
  viewFilters,
  setViewFilters,
}: UseOverviewContractDefaultsParams) => {
  const lastAppliedContractId = useRef<number | null>(null);

  const selectedContractId = viewFilters?.communityContractType;
  const selectedContractSlug = viewFilters?.communityContractSlugs?.[0];
  const matchedContract = useMemo(() => {
    if (!community?.contracts?.length) {
      return undefined;
    }
    return community.contracts.find(
      (contract: CommunityContract) =>
        (selectedContractId && contract.id === selectedContractId) ||
        (selectedContractSlug && contract.slug === selectedContractSlug)
    );
  }, [community?.contracts, selectedContractId, selectedContractSlug]);

  const hasSelection = Boolean(selectedContractId) || Boolean(selectedContractSlug);

  const shouldApply = useMemo(() => {
    if (!community || !isCommunityCCRC(community?.rental_types || [])) {
      return false;
    }
    if (!viewFilters) {
      return false;
    }
    if (!hasSelection) {
      return false;
    }
    return !matchedContract;
  }, [community, viewFilters, hasSelection, matchedContract]);

  useEffect(() => {
    if (!shouldApply) {
      if (matchedContract) {
        lastAppliedContractId.current = matchedContract.id ?? null;
      } else if (!hasSelection) {
        lastAppliedContractId.current = null;
      }
      return;
    }

    const defaultContract = getDefaultContract(community, competitors);
    if (!defaultContract) {
      return;
    }

    if (lastAppliedContractId.current === defaultContract.id) {
      return;
    }

    lastAppliedContractId.current = defaultContract.id || null;

    setViewFilters((prevState: any) => ({
      ...prevState,
      communityContractType: defaultContract.id,
      communityContractSelectionType:
        prevState?.communityContractSelectionType ?? COMMUNITY_CONTRACT_SELECTION_TYPE.APPROXIMATE,
      communityContractSlugs: defaultContract.slug ? [defaultContract.slug] : [],
      contractTypes: defaultContract.contract_id ? [defaultContract.contract_id] : [],
    }));
  }, [shouldApply, community, competitors, setViewFilters, viewFilters]);
};

export default useOverviewContractDefaults;
